import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Collapse,
  IconButton,
  TextField,
} from '@mui/material';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { keyframes } from '@mui/system';
import { updateRestaurantOrderStatus } from '../../api/orderApi';
import useRestaurantNotificationStore from '../../stores/restaurantNotificationStore';

// Note: Most logic, including data fetching and socket handling, has been moved to the parent component (RestaurantSection.jsx)

const STATUS_FLOW = [
  'pending_confirmation',
  'confirmed',
  'cooking',
  'cooked',
  'delivered',
];

const STATUS_META = {
  pending_confirmation: { label: 'Awaiting confirmation', color: 'warning' },
  confirmed: { label: 'Confirmed', color: 'info' },
  cooking: { label: 'In preparation', color: 'primary' },
  cooked: { label: 'Ready', color: 'success' },
  delivered: { label: 'Delivered', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

const STATUS_TRANSITIONS = {
  pending_confirmation: { next: 'confirmed', label: 'Confirm order' },
  confirmed: { next: 'cooking', label: 'Start preparation' },
  cooking: { next: 'cooked', label: 'Mark as Ready' },
  cooked: { next: 'delivered', label: 'Mark as Delivered' },
  delivered: null,
};

const shimmerBorder = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending_confirmation', label: 'Awaiting' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cooking', label: 'In preparation' },
  { value: 'cooked', label: 'Ready' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'delivered', label: 'Delivered' },
];

const formatMoney = (value) => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
    }).format(n);
  } catch (err) {
    return `$${n.toFixed(2)}`;
  }
};

function InfoItem({ label, value, secondary }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
      {secondary ? (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );
}

function StatusTimeline({ status }) {
  const activeIndex = useMemo(() => {
    const idx = STATUS_FLOW.indexOf(String(status));
    return idx >= 0 ? idx : 0;
  }, [status]);

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {STATUS_FLOW.map((key, idx) => {
          const completed = idx < activeIndex;
          const current = idx === activeIndex;
          return (
            <React.Fragment key={key}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: completed || current ? '#C8A86B' : '#E5E7EB',
                  color: completed || current ? 'white' : '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {idx + 1}
              </Box>
              {idx !== STATUS_FLOW.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    backgroundColor: idx < activeIndex ? '#C8A86B' : '#E5E7EB',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        {STATUS_FLOW.map((key) => (
          <Typography key={key} variant="caption" color="text.secondary">
            {STATUS_META[key]?.label || key}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

const CANCELLATION_REASONS = ['Guest request', 'Item not available', 'Kitchen closed', 'Other'];

export default function RestaurantOrders({ restaurantId, orders, loading, error, onRefresh, onUpdateOrder: onUpdateOrderProp }) {
  const [updating, setUpdating] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const reasonInputRef = React.useRef(null);
  const { clearNewOrderCount } = useRestaurantNotificationStore();

  const onUpdateOrder = useCallback((...args) => {
    if (typeof onUpdateOrderProp === 'function') {
      onUpdateOrderProp(...args);
    }
  }, [onUpdateOrderProp]);

  const toggleExpanded = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleRequestCancel = useCallback((order) => {
    setCancelTarget(order);
    setCancellationReason('');
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    const order = cancelTarget;
    try {
      setUpdating((prev) => ({ ...prev, [order._id]: true }));
      const res = await updateRestaurantOrderStatus(order._id, 'cancelled', 'Cancelled by staff', cancellationReason);
      onUpdateOrder(res?.data?.order || res?.data || { ...order, status: 'cancelled' });
    } catch (err) {
      console.error('[RestaurantOrders] cancel failed', err);
    } finally {
      setUpdating((prev) => {
        const nextState = { ...prev };
        delete nextState[order._id];
        return nextState;
      });
      setCancelTarget(null);
      setCancellationReason('');
    }
  }, [cancelTarget, onUpdateOrder, setUpdating, cancellationReason]);

  useEffect(() => {
    if (restaurantId) {
      clearNewOrderCount(restaurantId);
    }
  }, [restaurantId, clearNewOrderCount]);

  const visibleOrders = useMemo(() => {
    const safeOrders = Array.isArray(orders) ? orders : [];
    if (statusFilter === 'all') return safeOrders;
    return safeOrders.filter((order) => (order?.status || 'pending_confirmation') === statusFilter);
  }, [orders, statusFilter]);

  return (
    <Box sx={{ pt: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Restaurant Orders
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={(_e, val) => {
              if (val !== null) setStatusFilter(val);
            }}
          >
            {STATUS_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button variant="outlined" onClick={onRefresh} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {!restaurantId ? (
        <Typography color="text.secondary">Select a restaurant to view orders.</Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading orders…</Typography>
        </Box>
      ) : error ? (
        <Typography color="error.main">{error}</Typography>
      ) : visibleOrders.length === 0 ? (
        <Typography color="text.secondary">
          {statusFilter === 'all' ? 'No orders recorded yet.' : 'No orders found for this filter.'}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {visibleOrders.map((order) => {
            const statusKey = order?.status || 'pending_confirmation';
            const meta = STATUS_META[statusKey] || { label: statusKey, color: 'default' };
            const transition = STATUS_TRANSITIONS[statusKey];
            const isUpdating = !!updating[order._id];

            return (
              <Paper key={order._id} elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      #{String(order?._id || '').slice(-6)}
                    </Typography>
                    {order?.roomNumber ? (
                      <Typography variant="caption" color="text.secondary">
                        Room {order.roomNumber}
                      </Typography>
                    ) : null}
                  </Box>
                  <Chip label={meta.label} color={meta.color} size="small" />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <InfoItem label="Guest" value={order?.guestName || '—'} secondary={order?.contact} />
                  <InfoItem label="Total" value={formatMoney(order?.total)} />
                  <InfoItem label="Created" value={formatDateTime(order?.createdAt)} />
                </Box>

                <StatusTimeline status={statusKey} />

                {Array.isArray(order?.items) && order.items.length > 0 ? (
                  <Box sx={{ mt: 1.5, mb: 1 }}>
                    <Box
                      onClick={() => toggleExpanded(order._id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Items ({order.items.length})
                      </Typography>
                      <IconButton size="small" disableRipple>
                        <ExpandMoreIcon
                          fontSize="small"
                          style={{
                            transition: 'transform 200ms',
                            transform: expanded[order._id] ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </IconButton>
                    </Box>

                    <Collapse in={!!expanded[order._id]} timeout="auto" unmountOnExit>
                      <Stack spacing={1} sx={{ mt: 0.5 }}>
                        {order.items.map((it, idx) => (
                          <Box key={(it.menuItemId || it._id || it.name || 'item') + String(idx)}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              {it?.image ? (
                                <Box
                                  component="img"
                                  src={it.image}
                                  alt={it?.name || 'item'}
                                  loading="lazy"
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'background.paper',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.100',
                                  }}
                                />
                              )}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {it?.name || '—'} {Number(it?.qty) > 1 ? `× ${Number(it.qty)}` : ''}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {formatMoney((Number(it?.price) || 0) * (Number(it?.qty) || 1))}
                              </Typography>
                            </Stack>
                            {Array.isArray(it?.selectedOptions) && it.selectedOptions.length > 0 ? (
                              <Box sx={{ mt: 0.25, ml: '46px', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {it.selectedOptions.map((opt, oi) => (
                                  <Chip
                                    key={(opt?.name || 'opt') + String(oi)}
                                    label={`${opt?.groupName ? `${opt.groupName}: ` : ''}${opt?.name || ''}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            ) : null}
                            {idx !== order.items.length - 1 ? (
                              <Divider light sx={{ mt: 1, mb: 0.5 }} />
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                ) : null}

                {transition ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
                    {statusKey === 'pending_confirmation' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={isUpdating}
                        onClick={() => handleRequestCancel(order)}
                        sx={{
                          mt: 1.25,
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: '#fca5a5',
                          color: '#b91c1c',
                          '&:hover': {
                            backgroundColor: '#fef2f2',
                            borderColor: '#ef4444',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={isUpdating}
                      onClick={async () => {
                        try {
                          setUpdating((prev) => ({ ...prev, [order._id]: true }));
                          const res = await updateRestaurantOrderStatus(order._id, transition.next);
                          onUpdateOrder(res?.data?.order || res?.data);
                        } catch (err) {
                          console.error('[RestaurantOrders] failed to advance status', err);
                        } finally {
                          setUpdating((prev) => {
                            const nextState = { ...prev };
                            delete nextState[order._id];
                            return nextState;
                          });
                        }
                      }}
                      sx={{
                        mt: 1.25,
                        borderRadius: 2,
                        px: 1.75,
                        py: 0.6,
                        textTransform: 'none',
                        fontWeight: 500,
                        backgroundColor: '#FFFFFF',
                        color: '#111827',
                        ...(transition?.next === 'confirmed'
                          ? {
                              border: '2px solid transparent',
                              backgroundImage:
                                'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(90deg, #c8a86b 0%, #e0c98d 50%, #c8a86b 100%)',
                              backgroundOrigin: 'border-box',
                              backgroundClip: 'padding-box, border-box',
                              animation: `${shimmerBorder} 2s linear infinite`,
                              backgroundSize: '200% 100%',
                              '&:hover': {
                                backgroundImage:
                                  'linear-gradient(#f9fafb, #f9fafb), linear-gradient(90deg, #c8a86b 0%, #e0c98d 50%, #c8a86b 100%)',
                              },
                            }
                          : {
                              border: '1px solid #E5E7EB',
                              '&:hover': {
                                borderColor: '#D1D5DB',
                                backgroundColor: '#F9FAFB',
                              },
                            }),
                      }}
                    >
                      {transition.label}
                    </Button>
                  </Box>
                ) : (
                  statusKey === 'delivered' && <Chip label="Completed" color="success" variant="outlined" size="small" sx={{ mt: 1 }} />
                )}

                {order?.dietaryNote ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(200,168,107,0.08)',
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Dietary Note
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.dietaryNote}
                    </Typography>
                  </Box>
                ) : null}

                {statusKey === 'cancelled' && order.cancellationReason && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(220, 38, 38, 0.05)',
                      borderRadius: 1.5,
                      border: '1px solid rgba(220, 38, 38, 0.1)',
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="error.dark">
                      Cancellation Reason
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      {order.cancellationReason}
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
      {/* ConfirmDialog for cancelling order */}
      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel this order?"
        description={
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will mark the restaurant order as cancelled. Guests will no longer see it as active.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {CANCELLATION_REASONS.map((reason) => (
                <Chip
                  key={reason}
                  label={reason}
                  onClick={() => {
                    if (reason === 'Other') {
                      setCancellationReason('');
                      setTimeout(() => reasonInputRef.current?.focus(), 100);
                    } else {
                      setCancellationReason(reason);
                    }
                  }}
                  color={CANCELLATION_REASONS.includes(cancellationReason) && cancellationReason === reason ? 'primary' : 'default'}
                  variant={CANCELLATION_REASONS.includes(cancellationReason) && cancellationReason === reason ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
            <TextField
              inputRef={reasonInputRef}
              autoFocus
              margin="dense"
              id="cancellation-reason"
              label="Reason for cancellation"
              type="text"
              fullWidth
              variant="outlined"
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        }
        confirmLabel="Yes, cancel it"
        cancelLabel="No, keep it"
        onClose={() => {
          setCancelTarget(null);
          setCancellationReason('');
        }}
        onCancel={() => {
          setCancelTarget(null);
          setCancellationReason('');
        }}
        onConfirm={handleConfirmCancel}
        confirmDisabled={!cancellationReason.trim()}
      />
    </Box>
  );
}
const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
